// FilterChip component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.3

import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { tokens } from '../../../styles/tokens'
import { FilterChipProps } from './filter-chip.types'

// Styled chip container
const ChipContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  padding: '0 12px',
  borderRadius: 20,
  cursor: 'pointer',
  transition: `all ${tokens.transitions.fast}`,
  userSelect: 'none',
  border: `1px solid ${active ? tokens.colors.primary[300] : tokens.colors.grey[300]}`,
  backgroundColor: active ? tokens.colors.primary[50] : 'transparent',
  color: active ? tokens.colors.primary[600] : tokens.colors.grey[600],

  '&:hover': {
    borderColor: active ? tokens.colors.primary[400] : tokens.colors.grey[400],
    backgroundColor: active ? tokens.colors.primary[100] : tokens.colors.grey[50],
  },
}))

// Styled dot indicator
const DotIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: active ? tokens.colors.primary[500] : tokens.colors.grey[400],
}))

// Styled count badge
const CountBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 9,
  fontSize: 11,
  fontWeight: 600,
  backgroundColor: active ? tokens.colors.primary[200] : tokens.colors.grey[200],
  color: active ? tokens.colors.primary[700] : tokens.colors.grey[600],
}))

export const FilterChip: React.FC<FilterChipProps> = ({
  label,
  active = false,
  onClick,
  showDot = false,
  count,
  sx,
  className,
  'data-cy': dataCy,
}) => {
  return (
    <ChipContainer
      active={active}
      onClick={onClick}
      sx={sx}
      className={className}
      data-cy={dataCy}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      {showDot && <DotIndicator active={active} />}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      {count !== undefined && <CountBadge active={active}>{count}</CountBadge>}
    </ChipContainer>
  )
}

FilterChip.displayName = 'FilterChip'

export default FilterChip
export type { FilterChipProps } from './filter-chip.types'
